"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StakeableLockedOutput = exports.Secp256k1OutputOwners = exports.Secp256k1Output = exports.UserOutput = exports.TransferableOutput = exports.Utxo = void 0;
const utils_1 = require("../utils");
const constants_1 = require("./constants");
const types_1 = require("./types");
class Utxo {
    transactionId;
    utxoIndex;
    assetId;
    output;
    sourceChain;
    constructor(transactionId, utxoIndex, assetId, output) {
        this.transactionId = transactionId;
        this.utxoIndex = utxoIndex;
        this.assetId = assetId;
        this.output = output;
    }
    getUniqueId() {
        return `${this.transactionId.value}_${this.utxoIndex}}`;
    }
    static parse(data) {
        let buffer = utils_1.JuneoBuffer.from(data);
        // The data could be provided with a codec, if it is a string then
        // we assume that there are extra codec bytes at the beginning and skip it.
        if (typeof data === 'string') {
            buffer = buffer.copyOf(2, buffer.length);
        }
        const reader = buffer.createReader();
        const transactionId = new types_1.TransactionId(reader.read(constants_1.TransactionIdSize).toCB58());
        const utxoIndex = reader.readUInt32();
        const assetId = new types_1.AssetId(reader.read(constants_1.AssetIdSize).toCB58());
        const outputBuffer = reader.readRemaining();
        const output = TransferableOutput.parseOutput(outputBuffer);
        return new Utxo(transactionId, utxoIndex, assetId, output);
    }
}
exports.Utxo = Utxo;
class TransferableOutput {
    assetId;
    output;
    constructor(assetId, output) {
        this.assetId = assetId;
        this.output = output;
    }
    serialize() {
        const outputBuffer = this.output.serialize();
        const buffer = utils_1.JuneoBuffer.alloc(constants_1.AssetIdSize + outputBuffer.length);
        buffer.write(this.assetId);
        buffer.write(outputBuffer);
        return buffer;
    }
    static comparator = (a, b) => {
        return utils_1.JuneoBuffer.comparator(a.serialize(), b.serialize());
    };
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const assetId = new types_1.AssetId(reader.read(constants_1.AssetIdSize).toCB58());
        return new TransferableOutput(assetId, this.parseOutput(reader.readRemaining()));
    }
    static parseOutput(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const typeId = reader.readUInt32();
        switch (typeId) {
            case constants_1.Secp256k1OutputTypeId: {
                return Secp256k1Output.parse(data);
            }
            case constants_1.StakeableLockedOutputTypeId: {
                return StakeableLockedOutput.parse(data);
            }
            default: {
                throw new utils_1.ParsingError(`unsupported output type id "${typeId}"`);
            }
        }
    }
}
exports.TransferableOutput = TransferableOutput;
class UserOutput extends TransferableOutput {
    isChange;
    constructor(assetId, output, isChange) {
        super(assetId, output);
        this.isChange = isChange;
    }
}
exports.UserOutput = UserOutput;
class Secp256k1Output {
    typeId = constants_1.Secp256k1OutputTypeId;
    amount;
    locktime;
    threshold;
    addresses;
    constructor(amount, locktime, threshold, addresses) {
        this.amount = amount;
        this.locktime = locktime;
        this.threshold = threshold;
        this.addresses = addresses.sort(types_1.Address.comparator);
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(
        // 4 + 8 + 8 + 4 + 4 = 28
        28 + constants_1.AddressSize * this.addresses.length);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt64(this.amount);
        buffer.writeUInt64(this.locktime);
        buffer.writeUInt32(this.threshold);
        buffer.writeUInt32(this.addresses.length);
        for (const address of this.addresses) {
            buffer.write(address);
        }
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.Secp256k1OutputTypeId);
        const amount = reader.readUInt64();
        const locktime = reader.readUInt64();
        const threshold = reader.readUInt32();
        const addressesCount = reader.readUInt32();
        const addresses = [];
        for (let i = 0; i < addressesCount; i++) {
            const address = new types_1.Address(reader.read(constants_1.AddressSize));
            addresses.push(address);
        }
        return new Secp256k1Output(amount, locktime, threshold, addresses);
    }
}
exports.Secp256k1Output = Secp256k1Output;
class Secp256k1OutputOwners {
    typeId = constants_1.Secp256k1OutputOwnersTypeId;
    locktime;
    threshold;
    addresses;
    constructor(locktime, threshold, addresses) {
        this.locktime = locktime;
        this.threshold = threshold;
        this.addresses = addresses.sort(types_1.Address.comparator);
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(
        // 4 + 8 + 4 + 4 = 20
        20 + constants_1.AddressSize * this.addresses.length);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt64(this.locktime);
        buffer.writeUInt32(this.threshold);
        buffer.writeUInt32(this.addresses.length);
        for (const address of this.addresses) {
            buffer.write(address);
        }
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.Secp256k1OutputOwnersTypeId);
        const locktime = reader.readUInt64();
        const threshold = reader.readUInt32();
        const addressesCount = reader.readUInt32();
        const addresses = [];
        for (let i = 0; i < addressesCount; i++) {
            const address = new types_1.Address(reader.read(constants_1.AddressSize));
            addresses.push(address);
        }
        return new Secp256k1OutputOwners(locktime, threshold, addresses);
    }
}
exports.Secp256k1OutputOwners = Secp256k1OutputOwners;
class StakeableLockedOutput {
    typeId = constants_1.StakeableLockedOutputTypeId;
    locktime;
    amount;
    threshold;
    addresses;
    constructor(locktime, amount, threshold, addresses) {
        this.locktime = locktime;
        this.amount = amount;
        this.threshold = threshold;
        this.addresses = addresses.sort(types_1.Address.comparator);
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(
        // 4 + 8 + 4 + 8 + 8 + 4 + 4 = 40
        40 + constants_1.AddressSize * this.addresses.length);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt64(this.locktime);
        const transferableOutput = new Secp256k1Output(this.amount, BigInt(0), this.threshold, this.addresses);
        buffer.write(transferableOutput);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.StakeableLockedOutputTypeId);
        const locktime = reader.readUInt64();
        const output = Secp256k1Output.parse(reader.readRemaining());
        return new StakeableLockedOutput(locktime, output.amount, output.threshold, output.addresses);
    }
}
exports.StakeableLockedOutput = StakeableLockedOutput;
